import * as React from 'react';
import { Table, Button, Form, Divider } from '@beehive/components';
import { isBlank, prettyBytes, financial, financialToNumber } from 'utils';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import messages from 'messages/Workbench/ProjectDetail';
import _ from 'lodash';
import { getCooperateAccountsQuoteData } from '../../utils';
import DotDotDot from 'components/DotDotDot';
import NewPendingQuote from '@mpc/common/src/features/NewPendingQuote';
import VIewBoostWorks from '@mpc/common/src/features/VIewBoostWorks';


export interface SettleInfoProps {
  prefixCls: string;
  settleInfoConfig?: any;
}

const TABLE_COLUMNS = [
  { key: 'index', title: 'index', align: 'left', width: 80 },
  { key: 'platform', title: 'superior_resource_id', align: 'left', width: 100 },
  { key: 'account', title: 'account', align: 'left', width: 100 },
  { key: 'remark', title: 'remark', align: 'left', width: 100 },
  { key: 'quote_price', title: 'quote_price_rmb', align: 'right', width: 100 },
];
const FULL_COL_PROPS = {
  itemColProps: { span: 24 },
  itemLabelColProps: { span: 3 },
  itemInputColProps: { span: 21 },
};
const ONE_THIRD_COL_PROPS = {
  itemColProps: { span: 8 },
  itemLabelColProps: { span: 9 },
  itemInputColProps: { span: 15 },
};
const SummaryFormProps = {
  mode: 'show',
  isLabelVertical: false,
  className: 'basics',
  ...ONE_THIRD_COL_PROPS,
};
const SummaryFormItems = [
  { key: 'evaluate_scores', label: 'evaluate_scores' },
  { key: 'settle_price', label: 'settle_price' },
];
const AttachsFormProps = {
  mode: 'show',
  isLabelVertical: false,
  className: 'attachs',
  ...FULL_COL_PROPS,
};
const AttachsFormItems = [
  { key: 'settle_report_attachs', label: 'settle_report_attachs' },
];

class SettleInfo extends React.Component<SettleInfoProps, any> {

  public static contextTypes = {
    intl: PropTypes.object,
    detail: PropTypes.any,
    getAttachementDownloadLink: PropTypes.func,
  };

  private onTableRow = (row: any, index: number) => {
    return {
      className: classnames({
        is_win: row.is_win,
      }),
    };
  }

  private getTableColumnDefaultRender = () => {
    return (v: any, r: any, i: number) => {
      return isBlank(v) ? r.isTotal ? '' : '--' : v;
    };
  }

  private getTableColumnRender = () => {
    const { intl } = this.context;
    return {
      index: (v: any, r: any, i: number) => {
        return r.isTotal
          ? intl.formatMessage(messages.quote_price_total)
          : isBlank(v)
            ? '--'
            : v;
      },
      quote_price: (v: any, r: any, i: number) => {
        return isBlank(v) || v === 0 ? '--' : financial(v);
      },
      program_desc: (v: any, r: any, i: number) => {
        return isBlank(v)
          ? '--'
          : <DotDotDot height={3}>{v}</DotDotDot>;
      },
    };
  }

  private getTableColumns = () => {
    const { intl, detail } = this.context;
    const { settleInfoConfig } = this.props;
    const defaultRender = this.getTableColumnDefaultRender();
    const columRenders = this.getTableColumnRender();
    let tableColumns = TABLE_COLUMNS;
    if (settleInfoConfig && settleInfoConfig.getAppendTableColumns) {
      tableColumns = settleInfoConfig.getAppendTableColumns([...tableColumns], detail);
    }
    return tableColumns.map((item: any) => {
      return {
        ...item,
        title: intl.formatMessage(messages[item.title]),
        dataIndex: item.key,
        render: columRenders[item.key] || defaultRender,
        className: classnames({ [item.key]: true }),
      };
    });
  }

  public render() {
    const { intl, detail = {}, getAttachementDownloadLink } = this.context;
    const { prefixCls } = this.props;

    const className = `${prefixCls}-settle-info`;
    const tableColumns = this.getTableColumns();
    const tableData: any[] = getCooperateAccountsQuoteData(detail, intl, messages);
    const hasSummary = AttachsFormItems.some((i: any) => !isBlank(detail[i.key]));
    const hasAttachs = AttachsFormItems.some((i: any) => !isBlank(detail[i.key]));

    return (
      <div className={className}>
        {
          detail?.structured ?
            <NewPendingQuote
              detail={detail}
              view={true}
              showLastRound={false}
              value={detail?.demand_suppliers?.[0]}
            /> :
            <Table
              columns={tableColumns}
              data={tableData}
              size="small"
              rowKey="index"
              onRow={this.onTableRow}
              className={classnames({
                [`only-table`]: !hasSummary && !hasAttachs,
              })}
            />}

        {hasSummary && <Form {...SummaryFormProps}>
          {SummaryFormItems.map((item: any) => {
            const label = `${intl.formatMessage(messages[item.label])}${intl.formatMessage(messages.colon)}`;
            const value = detail[item.key];
            if (isBlank(value)) {
              return null;
            }
            let text: any = '';
            if (item.key === 'settle_price') {
              if (typeof value === 'string') {
                text = financial(financialToNumber(value) || 0);
              } else if (typeof value === 'number') {
                text = financial(value);
              } else {
                text = value;
              }
            } else if (item.key === 'evaluate_scores') {
              text = value.reduce((prev: any, curr: any) => {
                prev = _.add(prev, curr.real_score);
                return prev;
              }, 0);
            }
            return (
              <Form.Item {...item} key={item.key} label={label}>
                <div>{isBlank(text) ? '--' : text}</div>
              </Form.Item>
            );
          })}
        </Form>}
        {hasAttachs && <Divider />}
        {hasAttachs && <Form {...AttachsFormProps}>
          {AttachsFormItems.map((item: any) => {
            const label = `${intl.formatMessage(messages[item.label])}${intl.formatMessage(messages.colon)}`;
            const value = detail[item.key] || [];
            return (
              <Form.Item {...item} key={item.key} label={label}>
                <div>
                  {isBlank(value) ? '--' : value.map((file: any, index: number) => {
                    const { id, blob = {} } = file;
                    const { filename = '', byte_size = 0 } = blob;
                    return (
                      <div key={id} className="file-item">
                        {`${index + 1}${intl.formatMessage(messages.comma_alias)}${filename}`}
                        <span className="file-size">
                          {`${intl.formatMessage(messages.bracketsWrap, { content: prettyBytes(byte_size) })}`}
                        </span>
                        <Button link href={getAttachementDownloadLink(id)} target="_blank">
                          {intl.formatMessage(messages.download)}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </Form.Item>
            );
          })}
        </Form>}

        {detail?.product_area === 'DOMESTIC' && detail?.need_boost_effect_data && <Form
          {...AttachsFormProps}
          className='view-boost-works-form'
        >
          <Form.Item label='上传加热助推效果：'>
            <VIewBoostWorks style={{ marginTop: 0, marginLeft: '4px' }} detail={detail} />
          </Form.Item>
        </Form>}
      </div>
    );
  }

}

export default SettleInfo;
